import type {
  Equipment,
  EquipmentSnapshot,
  EquipmentRepository,
  EquipmentDocument,
  EquipmentStatus,
  MeasurementValue,
  MinimumThicknessRecord,
  EquipmentDamageMechanism
} from "./equipment-domain.js";
import { PermissionCode } from "../domain/identity/access-types.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { ClientId } from "../shared/kernel/identifiers.js";
import type { AuthorizationService } from "../permissions/authorization.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";
import { AuditAction } from "../audit/audit-log.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";

export interface EquipmentHistoryChecker {
  hasTechnicalHistory(clientId: string, equipmentId: string): Promise<{
    hasHistory: boolean;
    details: {
      inspectionsCount: number;
      snapshotsCount: number;
      reportsCount: number;
      recommendationsCount: number;
      avrCount: number;
      documentsCount: number;
      linkedInstrumentsCount?: number;
      linkedDevicesCount?: number;
    };
  }>;
  deleteDependencies?(clientId: string, equipmentId: string): Promise<void>;
}

export interface DeleteEligibilityResponse {
  canDelete: boolean;
  hasReport: boolean;
  hasDependencies: boolean;
  blockReason?: string;
}

/**
 * Validates and normalizes numeric measurement fields (Pressure, Temperature, Thickness, etc.)
 * Strictly rejects NaN, Infinity, and invalid string formats.
 */
export function validateMeasurementValue(val: any, fieldName: string, options?: { allowNegative?: boolean }): MeasurementValue | null {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object' && ('value' in val || 'unit' in val)) {
    if (val.value === null || val.value === undefined || val.value === '') return null;
    const num = typeof val.value === 'number' ? val.value : Number(String(val.value).replace(',', '.').trim());
    if (Number.isNaN(num) || !Number.isFinite(num)) {
      throw new AppError(ErrorCode.Validation, `Valor numérico inválido para ${fieldName}: "${val.value}"`, {
        safeMessage: `O campo ${fieldName} contém um valor numérico inválido (não aceita NaN, Infinity ou texto inválido).`
      });
    }
    if (!options?.allowNegative && num < 0) {
      throw new AppError(ErrorCode.Validation, `Valor negativo não permitido para ${fieldName}: ${num}`, {
        safeMessage: `O campo ${fieldName} não pode ser negativo.`
      });
    }
    return { value: num, unit: String(val.unit || '').trim() };
  }
  if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
    const num = typeof val === 'number' ? val : Number(String(val).replace(',', '.').trim());
    if (Number.isNaN(num) || !Number.isFinite(num)) {
      throw new AppError(ErrorCode.Validation, `Valor numérico inválido para ${fieldName}: "${val}"`, {
        safeMessage: `O campo ${fieldName} contém um valor numérico inválido (não aceita NaN, Infinity ou texto inválido).`
      });
    }
    if (!options?.allowNegative && num < 0) {
      throw new AppError(ErrorCode.Validation, `Valor negativo não permitido para ${fieldName}: ${num}`, {
        safeMessage: `O campo ${fieldName} não pode ser negativo.`
      });
    }
    return { value: num, unit: '' };
  }
  return null;
}

/**
 * Validates minimum required thickness record.
 */
export function validateMinimumThickness(rec: any): MinimumThicknessRecord | null {
  if (!rec) return null;
  const val = validateMeasurementValue(rec, 'Espessura Mínima Requerida');
  if (!val || val.value === null) return null;
  return {
    value: val.value,
    unit: val.unit || 'mm',
    source: rec.source === 'FORMULA_ENGINE' ? 'FORMULA_ENGINE' : (rec.source === 'CALCULATED' ? 'CALCULATED' : 'MANUAL'),
    responsible: rec.responsible ?? null,
    date: rec.date ?? new Date().toISOString(),
    calculationBasis: rec.calculationBasis ?? null
  };
}

/**
 * Validates Damage Mechanisms (Structured 1:N).
 * Rule: a sugestão automática (INSPECT IA REVIEW; o legado do Córtex também) entra como SUSPEITO e nunca é confirmada sem ação humana.
 */
export function validateDamageMechanisms(mechanisms?: any[] | null): EquipmentDamageMechanism[] {
  if (!mechanisms || !Array.isArray(mechanisms)) return [];
  return mechanisms.map((m, idx) => {
    const isCortexSuggestion = m.source === 'AI_REVIEW_SUGGESTION' || m.source === 'CORTEX_SUGGESTION';
    // Sugerido automaticamente e nunca confirmado por uma pessoa: continua SUSPEITO.
    const status = (isCortexSuggestion && !m.confirmedBy) && m.status === 'CONFIRMED' 
      ? 'SUSPECTED' 
      : (m.status || 'SUSPECTED');

    return {
      id: m.id || `DM-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
      equipmentId: m.equipmentId || '',
      damageMechanismId: m.damageMechanismId || m.code || `DM-${idx}`,
      name: m.name || 'Mecanismo de Dano',
      status: status,
      source: m.source || 'MANUAL',
      identifiedAt: m.identifiedAt || new Date().toISOString(),
      identifiedBy: m.identifiedBy || (isCortexSuggestion ? 'INSPECT IA REVIEW (sugestão por regra)' : 'Técnico Autorizado'),
      confirmedBy: m.confirmedBy ?? null,
      confirmedAt: m.confirmedAt ?? null,
      notes: m.notes ?? null
    };
  });
}

export interface ProjectHierarchyChecker {
  getProject(id: string): Promise<any | null>;
  getUnitArea(id: string): Promise<any | null>;
}

export class EquipmentService {
  constructor(
    private readonly repo: EquipmentRepository,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder,
    private readonly historyChecker?: EquipmentHistoryChecker,
    private readonly projectChecker?: ProjectHierarchyChecker
  ) {}

  /**
   * O usuário pode acessar este Cliente? Injetado na composição.
   *
   * Sem ele, a busca por id continua ESTRITA ao Cliente ativo — o
   * comportamento seguro de antes. É por isso que o gate é opcional: nenhuma
   * composição ou teste que não o forneça fica menos protegido.
   */
  private clientAccessChecker: ((context: RequestContext, clientId: string) => Promise<boolean>) | null = null;
  private authorizedClientIdsResolver: ((context: RequestContext) => Promise<readonly ClientId[]>) | null = null;

  public attachClientAccessChecker(checker: (context: RequestContext, clientId: string) => Promise<boolean>): void {
    this.clientAccessChecker = checker;
  }

  /**
   * Resolve os Clientes que a sessão pode consultar quando a tela pede uma
   * listagem global (sem `activeClientId`). A lista global nunca pode ser
   * confundida com uma consulta sem filtro no repositório: ela é a união dos
   * Clientes autorizados, mantendo o isolamento de tenant.
   */
  public attachAuthorizedClientIdsResolver(
    resolver: (context: RequestContext) => Promise<readonly ClientId[]>
  ): void {
    this.authorizedClientIdsResolver = resolver;
  }

  /**
   * Localiza o equipamento pelo id.
   *
   * O Cliente da SESSÃO não é chave de busca — é pré-seleção de navegação.
   * Quem decide a visibilidade é o ACESSO DO USUÁRIO ao Cliente do ativo
   * (`UserClientAccess`, a restrição da seção 37.1), conferido pelo checker
   * injetado.
   *
   * ISOLAMENTO PRESERVADO: um usuário sem acesso ao Cliente do ativo recebe
   * `null`, exatamente como antes — a proteção contra IDOR continua. O que
   * mudou é que um usuário AUTORIZADO deixou de ser barrado só porque estava
   * com outro Cliente selecionado na tela.
   */
  private async findEquipmentById(context: RequestContext, id: string): Promise<Equipment | null> {
    const scoped = context.activeClientId
      ? await this.repo.getEquipment(context.activeClientId, id as any)
      : null;
    if (scoped) return scoped;

    const found = await this.repo.getEquipment('' as any, id as any);
    if (!found) return null;

    // Sem verificador de acesso, mantém-se o escopo estrito do Cliente ativo.
    if (!this.clientAccessChecker) return context.activeClientId ? null : found;

    try {
      return (await this.clientAccessChecker(context, String(found.clientId))) ? found : null;
    } catch {
      // Não foi possível apurar o acesso: nega. Falha fechada.
      return null;
    }
  }

  /**
   * ABRIR UM EQUIPAMENTO PELO ID.
   *
   * ────────────────────────────────────────────────────────────────────
   * O Cliente da SESSÃO não é chave de busca.
   * ────────────────────────────────────────────────────────────────────
   *
   * Antes a busca era feita com `context.activeClientId` como escopo, e o
   * repositório devolve `null` quando o Cliente não bate. Consequência: abrir
   * `/equipment/:id` de um ativo de OUTRO Cliente — ou estando sem Cliente
   * ativo na sessão — simplesmente "não encontrava" o equipamento, e a tela
   * não abria. O mesmo acontecia na geração da Minuta, que resolve o
   * equipamento já persistido no relatório: o relatório apontava para um id
   * real, e a leitura respondia que não existia.
   *
   * Um id é um identificador global. A busca localiza o registro; QUEM PODE
   * VÊ-LO é decidido pela permissão `equipment.read`, exigida acima, e pelo
   * escopo de Cliente do usuário — que é restrição, não chave de consulta
   * (mesma regra da seção 37.1).
   */
  public async getEquipment(context: RequestContext, id: string): Promise<Equipment | null> {
    this.auth.require(context, PermissionCode.EquipmentRead);

    const eq = await this.findEquipmentById(context, id);
    if (!eq) return null;

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Equipment",
        entityId: eq.id,
        previousState: null,
        newState: null,
        metadata: { action: "EQUIPMENT_VIEWED", tag: eq.tag }
      }).catch(() => {});
    }

    return eq;
  }

  public async listEquipments(
    context: RequestContext,
    filter?: { status?: EquipmentStatus | 'ALL'; clientId?: string; allClients?: boolean; projectId?: string | null }
  ): Promise<Equipment[]> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    const requestedClientId = filter?.allClients ? null : (filter?.clientId || context.activeClientId);
    let list: Equipment[];

    if (requestedClientId) {
      // A seleção explícita de Cliente é validada antes da consulta: não basta
      // confiar no id enviado pela tela ou no filtro do repositório.
      if (this.clientAccessChecker && !(await this.clientAccessChecker(context, String(requestedClientId)))) {
        throw new AppError(ErrorCode.Authorization, "Cross-tenant equipment listing denied", {
          safeMessage: "Você não possui autorização para este Cliente."
        });
      }
      list = await this.repo.listEquipments(requestedClientId as ClientId);
    } else if (this.authorizedClientIdsResolver) {
      // "Todos os Clientes" significa todos os Clientes AUTORIZADOS, nunca
      // `repo.listEquipments(undefined)` (que é uma consulta sem WHERE).
      const authorizedClientIds = [...new Set(
        (await this.authorizedClientIdsResolver(context)).map(clientId => String(clientId))
      )] as ClientId[];
      const results = await Promise.all(
        authorizedClientIds.map(clientId => this.repo.listEquipments(clientId))
      );
      list = results.flat();
    } else {
      // Composição sem resolver (testes/integrações antigas): falha fechada.
      // O comportamento anterior devolvia todos os tenants quando o contexto
      // estava sem Cliente ativo.
      list = [];
    }

    // FILTRO POR PROJETO: quando projectId está presente, recorta a lista
    // em memória (projectId está apenas no payload JSON, não em coluna indexada).
    if (filter?.projectId) {
      list = list.filter(eq => eq.projectId === filter.projectId);
    }

    if (!filter || !filter.status || filter.status === 'ALL') {
      return list;
    }
    return list.filter(eq => eq.status === filter.status);
  }

  public async createEquipment(context: RequestContext, equipment: Equipment): Promise<Equipment> {
    this.auth.require(context, PermissionCode.EquipmentCreate);
    if (!equipment.clientId || !String(equipment.clientId).trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O CLIENTE.", {
        safeMessage: "O campo Cliente é obrigatório."
      });
    }

    // MANDATORY HIERARCHY RULE: CLIENTE -> PROJETO -> UNIDADE/ÁREA -> EQUIPAMENTO
    if (!equipment.projectId || !String(equipment.projectId).trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O PROJETO.", {
        safeMessage: "Todo equipamento deve obrigatoriamente estar vinculado a um Projeto do Cliente."
      });
    }

    const resolvedUnitAreaId = equipment.unitAreaId || equipment.areaId;
    if (!resolvedUnitAreaId || !String(resolvedUnitAreaId).trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE A UNIDADE/ÁREA.", {
        safeMessage: "Todo equipamento deve obrigatoriamente estar vinculado a uma Unidade/Área do Projeto."
      });
    }
    equipment.unitAreaId = String(resolvedUnitAreaId);
    equipment.areaId = String(resolvedUnitAreaId) as any;

    // RIGID BACKEND VALIDATION: Project belongs to Client? Unit/Area belongs to Project?
    if (this.projectChecker) {
      const project = await this.projectChecker.getProject(equipment.projectId);
      if (!project) {
        throw new AppError(ErrorCode.Validation, `Projeto '${equipment.projectId}' não encontrado.`, {
          safeMessage: "O Projeto selecionado não foi encontrado no cadastro."
        });
      }
      if (project.clientId !== equipment.clientId) {
        throw new AppError(ErrorCode.Validation, `O Projeto '${project.name || project.code}' não pertence ao Cliente selecionado.`, {
          safeMessage: "Validação de vínculo: O Projeto selecionado não pertence ao Cliente informado."
        });
      }

      const unitArea = await this.projectChecker.getUnitArea(equipment.unitAreaId);
      if (!unitArea) {
        throw new AppError(ErrorCode.Validation, `Unidade/Área '${equipment.unitAreaId}' não encontrada.`, {
          safeMessage: "A Unidade/Área selecionada não foi encontrada no cadastro."
        });
      }
      if (unitArea.projectId !== equipment.projectId) {
        throw new AppError(ErrorCode.Validation, `A Unidade/Área '${unitArea.name || unitArea.code}' não pertence ao Projeto '${project.name || project.code}'.`, {
          safeMessage: "Validação de vínculo: A Unidade/Área selecionada não pertence ao Projeto informado."
        });
      }
    }

    if (context.activeClientId && equipment.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Tenant isolation violation", {
        safeMessage: "Cannot create equipment for another client"
      });
    }

    // Check unique tag within this client
    const existingList = await this.repo.listEquipments(equipment.clientId as any);
    const normalizedTag = (equipment.tag || "").trim().toUpperCase();
    // P0-02 V4: proteger contra registros legados/corrompidos com tag undefined no banco de teste.
const duplicate = existingList.find(e => (e.tag || '').trim().toUpperCase() === normalizedTag && e.id !== equipment.id);
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, `TAG '${equipment.tag}' já está cadastrado para este cliente`, {
        safeMessage: `TAG '${equipment.tag}' já existe neste cliente.`
      });
    }

    // MANDATORY RULE: Every Piping (PIPING_LINE) must link to a registered Piping System (PIPING_SYSTEM)
    let resolvedSystem = equipment.system;
    let resolvedPipingSystemId = equipment.pipingSystemId;
    if (equipment.equipmentType === 'PIPING_LINE') {
      // Legacy records may not have pipeSystemApplicability explicitly set. Infer it the same way
      // equipment-migration.ts does for localStorage data, so records that already carry a valid
      // system reference aren't aborted on save (Line List "trava" fix). Genuinely new/orphan lines
      // with no system reference at all still fall through to the explicit-selection requirement below.
      const effectiveApplicability = equipment.pipeSystemApplicability !== undefined
        ? equipment.pipeSystemApplicability
        : (equipment.pipingSystemId || (equipment.system && equipment.system.trim().length > 0 && equipment.system !== 'NÃO INFORMADO' && equipment.system !== '-'))
          ? 'APPLICABLE'
          : undefined;

      if (effectiveApplicability === 'NOT_APPLICABLE') {
        resolvedPipingSystemId = null;
        resolvedSystem = 'NÃO APLICÁVEL';
      } else if (effectiveApplicability === 'APPLICABLE') {
        const targetSystemRef = equipment.pipingSystemId || equipment.system;
        if (!targetSystemRef || !targetSystemRef.trim()) {
          throw new AppError(ErrorCode.Validation, "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'.", {
            safeMessage: "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'."
          });
        }
        const matchedSystem = existingList.find(e =>
          e.equipmentType === 'PIPING_SYSTEM' &&
          (e.id === targetSystemRef || e.tag.trim().toUpperCase() === targetSystemRef.trim().toUpperCase())
        );
        if (!matchedSystem) {
          throw new AppError(ErrorCode.Validation, `Sistema de Tubulação não encontrado: '${targetSystemRef}'. Cadastre ou selecione um Sistema de Tubulação (ST) válido.`, {
            safeMessage: `Sistema de Tubulação não encontrado. Toda Tubulação deve estar vinculada a um Sistema de Tubulação (ST) cadastrado.`
          });
        }
        resolvedPipingSystemId = matchedSystem.id;
        resolvedSystem = matchedSystem.tag;
      } else {
        throw new AppError(ErrorCode.Validation, "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'.", {
          safeMessage: "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'."
        });
      }
    }

    // Numeric validations
    const designPressure = validateMeasurementValue(equipment.designPressure, 'Pressão de Projeto', { allowNegative: false });
    const operatingPressure = validateMeasurementValue(equipment.operatingPressure, 'Pressão de Operação', { allowNegative: false });
    const designTemperature = validateMeasurementValue(equipment.designTemperature, 'Temperatura de Projeto', { allowNegative: true });
    const operatingTemperature = validateMeasurementValue(equipment.operatingTemperature, 'Temperatura de Operação', { allowNegative: true });
    const nominalThickness = validateMeasurementValue(equipment.nominalThickness, 'Espessura Nominal', { allowNegative: false });
    const corrosionAllowance = validateMeasurementValue(equipment.corrosionAllowance, 'Sobre-espessura para Corrosão', { allowNegative: false });
    const outsideDiameter = validateMeasurementValue(equipment.outsideDiameter, 'Diâmetro Externo', { allowNegative: false });
    const minimumRequiredThickness = validateMinimumThickness(equipment.minimumRequiredThickness);
    const damageMechanisms = validateDamageMechanisms(equipment.damageMechanisms);

    // NR-13 Applicability rule: if NOT_APPLICABLE, category is cleared
    const nr13Applicability = equipment.nr13Applicability || 'UNCLASSIFIED';
    const nr13Category = nr13Applicability === 'NOT_APPLICABLE' ? null : (equipment.nr13Category || null);

    // Procedência do cadastro (Bloco 04): o payloadJson é sempre reconstruído
    // aqui, então uma chave `source` enviada pelo chamador (hoje, a importação
    // de planilha) seria descartada junto. Ela é preservada — e só ela — para
    // que a origem do dado não se perca nem na criação nem na edição posterior.
    // Nenhum chamador anterior envia essa chave, então nada muda para eles.
    let carriedSource: unknown;
    try {
      const incoming = equipment.payloadJson ? JSON.parse(equipment.payloadJson) : null;
      if (incoming && typeof incoming === 'object' && 'source' in incoming) carriedSource = (incoming as Record<string, unknown>)['source'];
    } catch { /* payloadJson inválido do chamador não pode impedir o cadastro */ }

    // Package payloadJson structured mirror
    const payload = {
      ...(carriedSource === undefined ? {} : { source: carriedSource }),
      identification: {
        tag: normalizedTag,
        name: equipment.name,
        type: equipment.equipmentType,
        service: equipment.service,
        lineNumber: equipment.lineNumber,
        fromEquipmentId: equipment.fromEquipmentId,
        fromReference: equipment.fromReference,
        toEquipmentId: equipment.toEquipmentId,
        toReference: equipment.toReference,
        fluid: equipment.fluid,
        fluidClass: equipment.fluidClass
      },
      project: {
        designPressure,
        designTemperature,
        material: equipment.material,
        nominalThickness,
        corrosionAllowance,
        fabricationStandard: equipment.fabricationStandard,
        projectDrawingFlowchart: equipment.projectDrawingFlowchart,
        nps: equipment.nps,
        schedule: equipment.schedule,
        outsideDiameter,
        insulation: equipment.insulation
      },
      operation: {
        operatingPressure,
        operatingTemperature,
        operatingCondition: equipment.operatingCondition,
        fluid: equipment.fluid,
        service: equipment.service
      },
      integrity: {
        nominalThickness,
        minimumRequiredThickness,
        corrosionAllowance,
        damageMechanisms
      },
      inService: {
        inServiceInspectionCode: equipment.inServiceInspectionCode,
        nr13Applicability,
        nr13Category
      }
    };
    
    const now = new Date().toISOString();
    const toSave: Equipment = {
      ...equipment,
      tag: normalizedTag,
      system: resolvedSystem,
      pipingSystemId: resolvedPipingSystemId ?? null,
      designPressure,
      operatingPressure,
      designTemperature,
      operatingTemperature,
      nominalThickness,
      corrosionAllowance,
      outsideDiameter,
      minimumRequiredThickness,
      damageMechanisms,
      nr13Applicability,
      nr13Category,
      payloadJson: JSON.stringify(payload),
      version: 1,
      createdAt: equipment.createdAt || now,
      updatedAt: now
    };

    await this.repo.saveEquipment(toSave);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Equipment",
        entityId: toSave.id,
        previousState: null,
        newState: {
          tag: toSave.tag,
          type: toSave.equipmentType,
          status: toSave.status,
          name: toSave.name
        },
        metadata: { action: "EQUIPMENT_CREATED" }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'EQUIPMENT', id: toSave.id } }));
    }

    return toSave;
  }

  public async updateEquipment(context: RequestContext, equipment: Equipment): Promise<Equipment> {
    this.auth.require(context, PermissionCode.EquipmentUpdate);
    if (equipment.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Tenant isolation violation", {
        safeMessage: "Cannot update equipment for another client"
      });
    }

    const existing = await this.repo.getEquipment(context.activeClientId, equipment.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found", {
        safeMessage: "Equipamento não encontrado."
      });
    }

    // MANDATORY HIERARCHY PRESERVATION: CLIENTE -> PROJETO -> UNIDADE/ÁREA
    const resolvedProjectId = equipment.projectId || existing.projectId;
    const resolvedUnitAreaId = equipment.unitAreaId || equipment.areaId || existing.unitAreaId || existing.areaId;
    
    if (!resolvedProjectId) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O PROJETO.", {
        safeMessage: "Todo equipamento deve obrigatoriamente estar vinculado a um Projeto do Cliente."
      });
    }
    if (!resolvedUnitAreaId) {
      throw new AppError(ErrorCode.Validation, "SELECIONE A UNIDADE/ÁREA.", {
        safeMessage: "Todo equipamento deve obrigatoriamente estar vinculado a uma Unidade/Área do Projeto."
      });
    }
    equipment.projectId = String(resolvedProjectId);
    equipment.unitAreaId = String(resolvedUnitAreaId);
    equipment.areaId = String(resolvedUnitAreaId) as any;

    // RIGID BACKEND VALIDATION: Project belongs to Client? Unit/Area belongs to Project?
    if (this.projectChecker) {
      const project = await this.projectChecker.getProject(equipment.projectId);
      if (!project) {
        throw new AppError(ErrorCode.Validation, `Projeto '${equipment.projectId}' não encontrado.`, {
          safeMessage: "O Projeto selecionado não foi encontrado no cadastro."
        });
      }
      if (project.clientId !== equipment.clientId) {
        throw new AppError(ErrorCode.Validation, `O Projeto '${project.name || project.code}' não pertence ao Cliente selecionado.`, {
          safeMessage: "Validação de vínculo: O Projeto selecionado não pertence ao Cliente informado."
        });
      }

      const unitArea = await this.projectChecker.getUnitArea(equipment.unitAreaId);
      if (!unitArea) {
        throw new AppError(ErrorCode.Validation, `Unidade/Área '${equipment.unitAreaId}' não encontrada.`, {
          safeMessage: "A Unidade/Área selecionada não foi encontrada no cadastro."
        });
      }
      if (unitArea.projectId !== equipment.projectId) {
        throw new AppError(ErrorCode.Validation, `A Unidade/Área '${unitArea.name || unitArea.code}' não pertence ao Projeto '${project.name || project.code}'.`, {
          safeMessage: "Validação de vínculo: A Unidade/Área selecionada não pertence ao Projeto informado."
        });
      }
    }

    // Check unique tag within this client if tag was changed
    const existingList = await this.repo.listEquipments(context.activeClientId);
    const normalizedTag = (equipment.tag || "").trim().toUpperCase();
    if (normalizedTag !== existing.tag.trim().toUpperCase()) {
      // P0-02 V4: proteger contra registros legados/corrompidos com tag undefined no banco de teste.
const duplicate = existingList.find(e => (e.tag || '').trim().toUpperCase() === normalizedTag && e.id !== equipment.id);
      if (duplicate) {
        throw new AppError(ErrorCode.Conflict, `TAG '${equipment.tag}' já está cadastrado para este cliente`, {
          safeMessage: `TAG '${equipment.tag}' já existe neste cliente.`
        });
      }
    }

    // MANDATORY RULE: Every Piping (PIPING_LINE) must link to a registered Piping System (PIPING_SYSTEM)
    let resolvedSystem = equipment.system;
    let resolvedPipingSystemId = equipment.pipingSystemId;
    if (equipment.equipmentType === 'PIPING_LINE') {
      // Legacy records may not have pipeSystemApplicability explicitly set. Infer it the same way
      // equipment-migration.ts does for localStorage data, so records that already carry a valid
      // system reference aren't aborted on save (Line List "trava" fix). Genuinely new/orphan lines
      // with no system reference at all still fall through to the explicit-selection requirement below.
      const effectiveApplicability = equipment.pipeSystemApplicability !== undefined
        ? equipment.pipeSystemApplicability
        : (equipment.pipingSystemId || (equipment.system && equipment.system.trim().length > 0 && equipment.system !== 'NÃO INFORMADO' && equipment.system !== '-'))
          ? 'APPLICABLE'
          : undefined;

      if (effectiveApplicability === 'NOT_APPLICABLE') {
        resolvedPipingSystemId = null;
        resolvedSystem = 'NÃO APLICÁVEL';
      } else if (effectiveApplicability === 'APPLICABLE') {
        const targetSystemRef = equipment.pipingSystemId || equipment.system;
        if (!targetSystemRef || !targetSystemRef.trim()) {
          throw new AppError(ErrorCode.Validation, "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'.", {
            safeMessage: "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'."
          });
        }
        const matchedSystem = existingList.find(e =>
          e.equipmentType === 'PIPING_SYSTEM' &&
          (e.id === targetSystemRef || e.tag.trim().toUpperCase() === targetSystemRef.trim().toUpperCase())
        );
        if (!matchedSystem) {
          throw new AppError(ErrorCode.Validation, `Sistema de Tubulação '${targetSystemRef}' não encontrado. Cadastre ou selecione um Sistema de Tubulação (ST) válido.`, {
            safeMessage: `Sistema de Tubulação não encontrado. Toda Tubulação deve estar vinculada a um Sistema de Tubulação (ST) cadastrado.`
          });
        }
        resolvedPipingSystemId = matchedSystem.id;
        resolvedSystem = matchedSystem.tag;
      } else {
        throw new AppError(ErrorCode.Validation, "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'.", {
          safeMessage: "Selecione o Sistema de Tubulação ao qual a linha pertence ou marque 'Não Aplicável'."
        });
      }
    }

    // Numeric validations
    const designPressure = validateMeasurementValue(equipment.designPressure, 'Pressão de Projeto', { allowNegative: false });
    const operatingPressure = validateMeasurementValue(equipment.operatingPressure, 'Pressão de Operação', { allowNegative: false });
    const designTemperature = validateMeasurementValue(equipment.designTemperature, 'Temperatura de Projeto', { allowNegative: true });
    const operatingTemperature = validateMeasurementValue(equipment.operatingTemperature, 'Temperatura de Operação', { allowNegative: true });
    const nominalThickness = validateMeasurementValue(equipment.nominalThickness, 'Espessura Nominal', { allowNegative: false });
    const corrosionAllowance = validateMeasurementValue(equipment.corrosionAllowance, 'Sobre-espessura para Corrosão', { allowNegative: false });
    const outsideDiameter = validateMeasurementValue(equipment.outsideDiameter, 'Diâmetro Externo', { allowNegative: false });
    const minimumRequiredThickness = validateMinimumThickness(equipment.minimumRequiredThickness);
    const damageMechanisms = validateDamageMechanisms(equipment.damageMechanisms);

    // NR-13 Applicability rule: if NOT_APPLICABLE, category is cleared
    const nr13Applicability = equipment.nr13Applicability || 'UNCLASSIFIED';
    const nr13Category = nr13Applicability === 'NOT_APPLICABLE' ? null : (equipment.nr13Category || null);

    // Procedência do cadastro (Bloco 04): o payloadJson é sempre reconstruído
    // aqui, então uma chave `source` enviada pelo chamador (hoje, a importação
    // de planilha) seria descartada junto. Ela é preservada — e só ela — para
    // que a origem do dado não se perca nem na criação nem na edição posterior.
    // Nenhum chamador anterior envia essa chave, então nada muda para eles.
    let carriedSource: unknown;
    try {
      const incoming = equipment.payloadJson ? JSON.parse(equipment.payloadJson) : null;
      if (incoming && typeof incoming === 'object' && 'source' in incoming) carriedSource = (incoming as Record<string, unknown>)['source'];
    } catch { /* payloadJson inválido do chamador não pode impedir o cadastro */ }

    // Package payloadJson structured mirror
    const payload = {
      ...(carriedSource === undefined ? {} : { source: carriedSource }),
      identification: {
        tag: normalizedTag,
        name: equipment.name,
        type: equipment.equipmentType,
        service: equipment.service,
        lineNumber: equipment.lineNumber,
        fromEquipmentId: equipment.fromEquipmentId,
        fromReference: equipment.fromReference,
        toEquipmentId: equipment.toEquipmentId,
        toReference: equipment.toReference,
        fluid: equipment.fluid,
        fluidClass: equipment.fluidClass
      },
      project: {
        designPressure,
        designTemperature,
        material: equipment.material,
        nominalThickness,
        corrosionAllowance,
        fabricationStandard: equipment.fabricationStandard,
        projectDrawingFlowchart: equipment.projectDrawingFlowchart,
        nps: equipment.nps,
        schedule: equipment.schedule,
        outsideDiameter,
        insulation: equipment.insulation
      },
      operation: {
        operatingPressure,
        operatingTemperature,
        operatingCondition: equipment.operatingCondition,
        fluid: equipment.fluid,
        service: equipment.service
      },
      integrity: {
        nominalThickness,
        minimumRequiredThickness,
        corrosionAllowance,
        damageMechanisms
      },
      inService: {
        inServiceInspectionCode: equipment.inServiceInspectionCode,
        nr13Applicability,
        nr13Category
      }
    };
    
    const now = new Date().toISOString();
    const toSave: Equipment = {
      ...equipment,
      tag: normalizedTag,
      system: resolvedSystem,
      pipingSystemId: resolvedPipingSystemId ?? null,
      designPressure,
      operatingPressure,
      designTemperature,
      operatingTemperature,
      nominalThickness,
      corrosionAllowance,
      outsideDiameter,
      minimumRequiredThickness,
      damageMechanisms,
      nr13Applicability,
      nr13Category,
      payloadJson: JSON.stringify(payload),
      version: (existing.version || 1) + 1,
      updatedAt: now
    };

    await this.repo.saveEquipment(toSave);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Equipment",
        entityId: toSave.id,
        previousState: { tag: existing.tag, status: existing.status, version: existing.version },
        newState: { tag: toSave.tag, status: toSave.status, version: toSave.version },
        metadata: { action: "EQUIPMENT_UPDATED" }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'EQUIPMENT', id: toSave.id } }));
    }

    return toSave;
  }

  /**
   * Controlled Deletion of Equipment:
   * 1. Checks if technical/documental history exists.
   * 2. If history exists -> REJECTS Hard Delete and instructs Inactivation.
   * 3. If no history exists -> Executes Hard Delete.
   */
  public async deleteEquipment(
    context: RequestContext,
    equipmentId: string,
    reason: string
  ): Promise<{ deleted: boolean; message: string }> {
    try {
      this.auth.require(context, PermissionCode.EquipmentDelete);
    } catch {
      this.auth.require(context, PermissionCode.EquipmentUpdate);
    }

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a exclusão."
      });
    }

    const eq = await this.repo.getEquipment(context.activeClientId, equipmentId as any);
    if (!eq) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found", {
        safeMessage: "Equipamento não encontrado."
      });
    }

    // Record delete request in audit log
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "Equipment",
        entityId: eq.id,
        previousState: { tag: eq.tag, status: eq.status },
        newState: null,
        metadata: { action: "EQUIPMENT_DELETE_REQUESTED", reason }
      }).catch(() => {});
    }

    // NEW DELETE LOGIC BASED ON REPORTS
    let reportsCount = 0;
    let dependenciesCount = 0;
    let historyDetails: any = null;

    if (this.historyChecker) {
      const check = await this.historyChecker.hasTechnicalHistory(context.activeClientId, equipmentId);
      historyDetails = check.details;
      reportsCount = check.details.reportsCount || 0;
      dependenciesCount = (check.details.inspectionsCount || 0) + 
                          (check.details.snapshotsCount || 0) + 
                          (check.details.recommendationsCount || 0) + 
                          (check.details.documentsCount || 0) + 
                          (check.details.linkedInstrumentsCount || 0) + 
                          (check.details.linkedDevicesCount || 0);
    }

    // Rule: if ANY report exists, BLOCK deletion.
    if (reportsCount > 0) {
      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Delete,
          entityType: "Equipment",
          entityId: eq.id,
          previousState: { tag: eq.tag, status: eq.status },
          newState: null,
          metadata: {
            action: "EQUIPMENT_DELETE_DENIED_REPORT_EXISTS",
            reason,
            historyDetails
          }
        }).catch(() => {});
      }

      throw new AppError(
        ErrorCode.Conflict,
        "NÃO É POSSÍVEL EXCLUIR ESTE EQUIPAMENTO",
        {
          safeMessage: "Este equipamento possui Relatório de Integridade vinculado e faz parte do histórico documental do INSPECT IA. Utilize a opção de inativação/arquivamento, quando disponível."
        }
      );
    }

    // Check if this is a PIPING_SYSTEM with linked PIPING_LINEs
    if (eq.equipmentType === 'PIPING_SYSTEM') {
      const allEqs = await this.repo.listEquipments(context.activeClientId);
      const linkedLines = allEqs.filter(e => 
        e.equipmentType === 'PIPING_LINE' && 
        (e.system === eq.tag || e.pipingSystemId === eq.id) &&
        e.status !== 'INACTIVE'
      );
      if (linkedLines.length > 0) {
        throw new AppError(
          ErrorCode.Conflict,
          `Este Sistema de Tubulação possui ${linkedLines.length} Tubulação(ões) vinculada(s). Exclua ou desvincule as tubulações antes de excluir o sistema.`,
          {
            safeMessage: `Este Sistema de Tubulação possui ${linkedLines.length} Tubulação(ões) vinculada(s) (${linkedLines.map(l => l.tag).join(', ')}). Não pode ser excluído.`
          }
        );
      }
    }

    // Auto-cascade deletion for allowed dependencies if no reports exist
    if (dependenciesCount > 0) {
      if (this.historyChecker && this.historyChecker.deleteDependencies) {
        await this.historyChecker.deleteDependencies(context.activeClientId, equipmentId);
      }
    }

    // Perform hard delete since no reports exist and dependencies were cascaded
    await this.repo.deleteEquipment(context.activeClientId, equipmentId as any);



    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "Equipment",
        entityId: eq.id,
        previousState: { tag: eq.tag, name: eq.name, status: eq.status },
        newState: null,
        metadata: {
          action: "EQUIPMENT_DELETED",
          deletedBy: context.userId,
          deletionReason: reason,
          deletedDependenciesCount: dependenciesCount
        }
      }).catch(() => {});
    }

    return {
      deleted: true,
      message: `Equipamento ${eq.tag} excluído definitivamente com sucesso.`
    };
  }

  /**
   * Inactivates an equipment with reason logging.
   */
  public async inactivateEquipment(
    context: RequestContext,
    equipmentId: string,
    reason: string
  ): Promise<Equipment> {
    try {
      this.auth.require(context, PermissionCode.EquipmentInactivate);
    } catch {
      this.auth.require(context, PermissionCode.EquipmentUpdate);
    }

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a inativação."
      });
    }

    const eq = await this.repo.getEquipment(context.activeClientId, equipmentId as any);
    if (!eq) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found", {
        safeMessage: "Equipamento não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: Equipment = {
      ...eq,
      status: 'INACTIVE',
      inactivatedAt: now,
      inactivatedBy: context.userId,
      inactivationReason: reason,
      version: (eq.version || 1) + 1,
      updatedAt: now
    };

    await this.repo.saveEquipment(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "Equipment",
        entityId: eq.id,
        previousState: { status: eq.status },
        newState: { status: 'INACTIVE' },
        metadata: {
          action: "EQUIPMENT_INACTIVATED",
          inactivatedBy: context.userId,
          inactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  public async checkDeleteEligibility(
    context: RequestContext,
    equipmentId: string
  ): Promise<DeleteEligibilityResponse> {
    try {
      this.auth.require(context, PermissionCode.EquipmentDelete);
    } catch {
      this.auth.require(context, PermissionCode.EquipmentUpdate);
    }

    const eq = await this.repo.getEquipment(context.activeClientId, equipmentId as any);
    if (!eq) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found");
    }

    let reportsCount = 0;
    let dependenciesCount = 0;
    
    if (this.historyChecker) {
      const check = await this.historyChecker.hasTechnicalHistory(context.activeClientId, equipmentId);
      reportsCount = check.details.reportsCount || 0;
      dependenciesCount = (check.details.inspectionsCount || 0) + 
                          (check.details.snapshotsCount || 0) + 
                          (check.details.recommendationsCount || 0) + 
                          (check.details.documentsCount || 0) + 
                          (check.details.linkedInstrumentsCount || 0) + 
                          (check.details.linkedDevicesCount || 0);
    } else {
      const snaps = await this.repo.listSnapshotsByEquipment(context.activeClientId, equipmentId as any);
      const docs = await this.repo.listDocuments(context.activeClientId, equipmentId as any);
      dependenciesCount = snaps.length + docs.length;
    }

    if (eq.equipmentType === 'PIPING_SYSTEM') {
      const allEqs = await this.repo.listEquipments(context.activeClientId);
      const linkedLines = allEqs.filter(e => 
        e.equipmentType === 'PIPING_LINE' && 
        (e.system === eq.tag || e.pipingSystemId === eq.id) &&
        e.status !== 'INACTIVE'
      );
      if (linkedLines.length > 0) {
         return {
           canDelete: false,
           hasReport: reportsCount > 0,
           hasDependencies: dependenciesCount > 0,
           blockReason: `Este Sistema de Tubulação possui ${linkedLines.length} Tubulação(ões) vinculada(s). Não pode ser excluído.`
         };
      }
    }

    if (reportsCount > 0) {
      return {
        canDelete: false,
        hasReport: true,
        hasDependencies: dependenciesCount > 0,
        blockReason: "Este equipamento possui Relatório de Integridade vinculado e faz parte do histórico documental do INSPECT IA. Utilize a opção de inativação/arquivamento, quando disponível."
      };
    }

    return {
      canDelete: true,
      hasReport: false,
      hasDependencies: dependenciesCount > 0
    };
  }

  /**
   * Reactivates an inactivated equipment.
   */
  public async reactivateEquipment(
    context: RequestContext,
    equipmentId: string,
    reason: string = "Reativação de cadastro"
  ): Promise<Equipment> {
    try {
      this.auth.require(context, PermissionCode.EquipmentReactivate);
    } catch {
      this.auth.require(context, PermissionCode.EquipmentUpdate);
    }

    const eq = await this.repo.getEquipment(context.activeClientId, equipmentId as any);
    if (!eq) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found", {
        safeMessage: "Equipamento não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: Equipment = {
      ...eq,
      status: 'ACTIVE',
      inactivatedAt: null,
      inactivatedBy: null,
      inactivationReason: null,
      version: (eq.version || 1) + 1,
      updatedAt: now
    };

    await this.repo.saveEquipment(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.StatusChange,
        entityType: "Equipment",
        entityId: eq.id,
        previousState: { status: eq.status },
        newState: { status: 'ACTIVE' },
        metadata: {
          action: "EQUIPMENT_REACTIVATED",
          reactivatedBy: context.userId,
          reactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  /**
   * Abre um lote de gravação (Bloco 04: importação em massa). Repositórios
   * que suportam a otimização suspendem a gravação intermediária até
   * `endBulkOperation`; os demais ignoram silenciosamente (no-op seguro).
   * Sempre finalize com `endBulkOperation` em `finally`, nunca condicional.
   */
  public beginBulkOperation(): void {
    this.repo.beginBatch?.();
  }

  /**
   * Fecha o lote. `commit=true` (padrão) força a gravação/confirmação real
   * e propaga o erro do repositório (por exemplo, armazenamento local
   * cheio, ou uma transação SQL que não pôde ser confirmada) para que o
   * chamador — hoje, `EquipmentImportService` — trate como falha crítica e
   * reverta o lote, em vez de reportar sucesso com dado que não persiste de
   * verdade. `commit=false` descarta o lote (equivalente a ROLLBACK, em
   * repositórios que suportam transação real).
   */
  public async endBulkOperation(commit = true): Promise<void> {
    await this.repo.endBatch?.(commit);
  }

  public async getSnapshot(context: RequestContext, snapshotId: string): Promise<EquipmentSnapshot | null> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    return this.repo.getSnapshot(snapshotId);
  }

  public async listSnapshots(context: RequestContext, equipmentId: string): Promise<EquipmentSnapshot[]> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    return this.repo.listSnapshotsByEquipment(context.activeClientId, equipmentId as any);
  }

  /**
   * Captures an immutable snapshot of the equipment's current state.
   */
  public async createSnapshot(
    context: RequestContext,
    equipmentId: string,
    reason: string
  ): Promise<EquipmentSnapshot> {
    this.auth.require(context, PermissionCode.EquipmentRead);

    // Mesma busca por id do `getEquipment`: o snapshot alimenta a Minuta e o
    // RI, que resolvem o equipamento JÁ PERSISTIDO no documento. Escopar pelo
    // Cliente da sessão fazia a geração falhar com "Equipamento não
    // encontrado" para um id que o próprio relatório havia gravado.
    const eq = await this.findEquipmentById(context, equipmentId);
    if (!eq) {
      throw new AppError(ErrorCode.NotFound, "Equipment not found", {
        safeMessage: "Equipamento não encontrado para gerar snapshot."
      });
    }

    const snapshotId = `SNAP-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`.toUpperCase();

    const eqData = JSON.parse(JSON.stringify(eq));
    if (this.projectChecker && eq.projectId) {
      try {
        const proj = await this.projectChecker.getProject(eq.projectId);
        if (proj) {
          eqData.projectName = proj.name;
          eqData.projectCode = proj.code;
        }
        const targetAreaId = eq.unitAreaId || eq.areaId;
        if (targetAreaId) {
          const unit = await this.projectChecker.getUnitArea(targetAreaId);
          if (unit) {
            eqData.unitAreaName = unit.name;
            eqData.unitAreaCode = unit.code;
          }
        }
      } catch {
        // Safe fallback
      }
    }

    const snapshot: EquipmentSnapshot = {
      id: snapshotId,
      clientId: context.activeClientId,
      equipmentId: eq.id,
      equipmentVersion: eq.version,
      payloadJson: JSON.stringify(eqData),
      equipmentData: eqData,
      capturedAt: new Date().toISOString(),
      reason
    };

    await this.repo.saveSnapshot(snapshot);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "EquipmentSnapshot",
        entityId: snapshot.id,
        previousState: null,
        newState: {
          equipmentId: eq.id,
          equipmentVersion: eq.version,
          capturedAt: snapshot.capturedAt
        },
        metadata: { action: "EQUIPMENT_SNAPSHOT_CREATED", reason }
      }).catch(() => {});
    }

    return snapshot;
  }

  // Document Management
  public async listDocuments(context: RequestContext, equipmentId: string): Promise<EquipmentDocument[]> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    return this.repo.listDocuments(context.activeClientId, equipmentId as any);
  }

  public async linkDocument(
    context: RequestContext,
    doc: EquipmentDocument
  ): Promise<EquipmentDocument> {
    this.auth.require(context, PermissionCode.EquipmentUpdate);
    await this.repo.saveDocument(doc);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "EquipmentDocument",
        entityId: doc.id,
        previousState: null,
        newState: { title: doc.title, type: doc.documentType, filename: doc.filename },
        metadata: { action: "EQUIPMENT_DOCUMENT_LINKED", equipmentId: doc.equipmentId }
      }).catch(() => {});
    }

    return doc;
  }

  public async unlinkDocument(
    context: RequestContext,
    equipmentId: string,
    documentId: string
  ): Promise<void> {
    this.auth.require(context, PermissionCode.EquipmentUpdate);
    await this.repo.removeDocument(context.activeClientId, documentId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "EquipmentDocument",
        entityId: documentId,
        previousState: null,
        newState: null,
        metadata: { action: "EQUIPMENT_DOCUMENT_UNLINKED", equipmentId }
      }).catch(() => {});
    }
  }
}

